import Button from '@ui/Button'
import Card from '@ui/Card'
import Field from '@ui/Field'
import Input from '@ui/Input'
import Sheet from '@ui/Sheet'
import type { User } from 'firebase/auth'
import { type FormEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  beginTotpEnrolment,
  IdpError,
  type IdpErrorCode,
  isIdpEmulator,
  removeTotp,
  signOutIdp,
  type TotpEnrolment,
} from '@/auth/idp'

import OneTimeCodeField from './OneTimeCodeField'
import ReauthForm, { idpErrorKey, type ReauthMethod } from './ReauthForm'

// The profile row already knows whether 2FA is on, so the sheet opens with an
// intent and goes straight from the password check to the QR code or to the
// turn-off confirmation. Firebase is signed out whenever the sheet closes.

export type TwoFactorIntent = 'enable' | 'disable'

type Step =
  | { kind: 'reauth' }
  | { kind: 'enrol'; enrolment: TotpEnrolment; qr: string }
  | { kind: 'confirm-disable'; user: User }

type Props = {
  open: boolean
  intent: TwoFactorIntent
  email: string
  /** Password users re-check with it; Google-only users sign in with Google again. */
  reauth: ReauthMethod
  /** Which methods sign in on their own once 2FA is off, for the warning. */
  methods: { password: boolean; google: boolean }
  onClose: () => void
  /** Called after 2FA was turned on or off, before the sheet closes. */
  onChanged: () => void
}

const TwoFactorSheet = ({
  open,
  intent,
  email,
  reauth,
  methods,
  onClose,
  onChanged,
}: Props) => {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>({ kind: 'reauth' })
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<IdpErrorCode | null>(null)
  const [onEmulator, setOnEmulator] = useState(false)

  useEffect(() => {
    let live = true
    isIdpEmulator().then((value) => live && setOnEmulator(value))
    return () => {
      live = false
    }
  }, [])

  useEffect(() => {
    if (open) return
    setStep({ kind: 'reauth' })
    setCode('')
    setError(null)
    signOutIdp()
  }, [open])

  const finish = () => {
    onChanged()
    onClose()
  }

  const onVerified = async (user: User) => {
    if (intent === 'disable') {
      setStep({ kind: 'confirm-disable', user })
      return
    }
    const enrolment = await beginTotpEnrolment(user, email)
    const { toDataURL } = await import('qrcode')
    const qr = await toDataURL(enrolment.otpauthUrl, { margin: 1, width: 200 })
    setStep({ kind: 'enrol', enrolment, qr })
  }

  const run = async (work: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await work()
    } catch (err) {
      setError(err instanceof IdpError ? err.code : 'unknown')
    } finally {
      setBusy(false)
    }
  }

  const onConfirmEnrol = (e: FormEvent) => {
    e.preventDefault()
    if (step.kind !== 'enrol') return
    run(async () => {
      await step.enrolment.confirm(code.trim())
      finish()
    })
  }

  const onDisable = () => {
    if (step.kind !== 'confirm-disable') return
    run(async () => {
      await removeTotp(step.user)
      finish()
    })
  }

  const errorLine = error && (
    <p role="alert" className="text-sm text-flag">
      {t(idpErrorKey(error))}
    </p>
  )

  const enableBlocked = intent === 'enable' && onEmulator
  let disableWarningKey = 'two-factor.disable-warning'
  if (methods.google) {
    disableWarningKey = methods.password
      ? 'two-factor.disable-warning-both'
      : 'two-factor.disable-warning-google'
  }
  let reauthIntro = t('two-factor.reauth-provider-intro', {
    provider: 'Google',
  })
  if (reauth === 'password') {
    reauthIntro =
      intent === 'enable'
        ? t('two-factor.confirm-password-enable')
        : t('two-factor.confirm-password-disable')
  }

  return (
    <Sheet open={open} onClose={onClose} title={t('two-factor.title')}>
      {enableBlocked && (
        <Card tone="info" padding="md">
          <p className="text-sm">{t('two-factor.emulator-unsupported')}</p>
        </Card>
      )}

      {!enableBlocked && step.kind === 'reauth' && (
        <ReauthForm
          email={email}
          method={reauth}
          intro={reauthIntro}
          onVerified={onVerified}
        />
      )}

      {step.kind === 'enrol' && (
        <form
          key="enrol"
          onSubmit={onConfirmEnrol}
          className="flex flex-col gap-4"
        >
          <p className="text-sm text-ink-700">{t('two-factor.scan')}</p>
          <img
            src={step.qr}
            alt={t('two-factor.qr-alt')}
            width={200}
            height={200}
            className="self-center rounded-lg border border-ink-100"
          />
          <Field label={t('two-factor.secret-key')}>
            <Input
              variant="mono"
              readOnly
              value={step.enrolment.secretKey}
              onFocus={(e) => e.currentTarget.select()}
            />
          </Field>
          <OneTimeCodeField value={code} onChange={setCode} />
          {errorLine}
          <Button type="submit" fullWidth disabled={busy || code.length < 6}>
            {t('two-factor.confirm')}
          </Button>
        </form>
      )}

      {step.kind === 'confirm-disable' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-ink-700">{t(disableWarningKey)}</p>
          {errorLine}
          <Button
            variant="destructive"
            fullWidth
            disabled={busy}
            onClick={onDisable}
          >
            {t('two-factor.remove')}
          </Button>
        </div>
      )}
    </Sheet>
  )
}

export default TwoFactorSheet
